import React, { useState, useEffect } from 'react';
import { X } from 'react-feather';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PathFinderLoader } from '../components/PathFinderLoader';
import { getProductImage } from '../utils/categoryUtils';

const AdminPanel = () => {
  // --- Data & UI state ---
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [blogs, setBlogs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(1); // 1=Products, 2=Blogs

  // --- Modal & form state ---
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [isBlogEditModalOpen, setIsBlogEditModalOpen] = useState(false);
  const [isBlogAddModalOpen, setIsBlogAddModalOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const [productFormData, setProductFormData] = useState({
    itemName: '',
    category: '',
    retailPrice: '',
    description: '',
    precautions: '',
    image: null,
  });
  const [blogFormData, setBlogFormData] = useState({
    title: '',
    subheading: '',
    content: '',
    image: null,
  });

  // --- Pagination state ---
  const PRODUCTS_PER_PAGE = 20;
  const [productPage, setProductPage] = useState(1);
  const totalProductPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);

  const BLOGS_PER_PAGE = 10;
  const [blogPage, setBlogPage] = useState(1);
  const totalBlogPages = Math.ceil(blogs.length / BLOGS_PER_PAGE);

  // Reset pages when data changes
  useEffect(() => {
    setProductPage(1);
  }, [filteredProducts]);
  useEffect(() => {
    setBlogPage(1);
  }, [blogs]);

  // --- Fetch data on mount ---
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [prodRes, catRes, blogRes] = await Promise.all([
          fetch('https://dawamaxma.dawamtaani.co.ke/api/pharmacy'),
          fetch('https://dawamaxma.dawamtaani.co.ke/api/pharmacy/categories'),
          fetch('https://dawamaxma.dawamtaani.co.ke/api/blogs'),
        ]);
        if (!prodRes.ok) throw new Error('Failed to fetch products');
        if (!catRes.ok) throw new Error('Failed to fetch categories');
        if (!blogRes.ok) throw new Error('Failed to fetch blogs');

        const [prods, cats, bls] = await Promise.all([
          prodRes.json(),
          catRes.json(),
          blogRes.json(),
        ]);
        setProducts(prods);
        setFilteredProducts(prods);
        setCategories(cats);
        setBlogs(bls);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // --- Filter products when search/category change ---
  useEffect(() => {
    const filtered = products.filter((p) => {
      const byCat = selectedCategory ? p.category === selectedCategory : true;
      const bySearch = searchQuery
        ? p.itemName.toLowerCase().includes(searchQuery.toLowerCase())
        : true;
      return byCat && bySearch;
    });
    setFilteredProducts(filtered);
  }, [searchQuery, selectedCategory, products]);

  // --- Handlers for opening/closing modals ---
  const handleOpenEditModal = (product) => {
    setSelectedProduct(product);
    setProductFormData({
      itemName: product.itemName,
      category: product.category,
      retailPrice: product.retailPrice,
      description: product.description || '',
      precautions: product.precautions || '',
      image: product.image || null,
    });
    setIsEditModalOpen(true);
  };
  const handleOpenAddModal = () => {
    setSelectedProduct(null);
    setProductFormData({
      itemName: '',
      category: '',
      retailPrice: '',
      description: '',
      precautions: '',
      image: null,
    });
    setIsAddModalOpen(true);
  };
  const handleOpenBlogEditModal = (blog) => {
    setSelectedBlog(blog);
    setBlogFormData({
      title: blog.title,
      subheading: blog.subheading,
      content: blog.content,
      image: blog.image || null,
    });
    setIsBlogEditModalOpen(true);
  };
  const handleOpenBlogAddModal = () => {
    setSelectedBlog(null);
    setBlogFormData({ title: '', subheading: '', content: '', image: null });
    setIsBlogAddModalOpen(true);
  };
  const handleCloseModals = () => {
    setIsEditModalOpen(false);
    setIsAddModalOpen(false);
    setIsBlogEditModalOpen(false);
    setIsBlogAddModalOpen(false);
    setSelectedProduct(null);
    setSelectedBlog(null);
  };

  // --- Form field handlers ---
  const handleInputChange = (e, isBlog = false) => {
    const { name, value } = e.target;
    if (isBlog) {
      setBlogFormData((fd) => ({ ...fd, [name]: value }));
    } else {
      setProductFormData((fd) => ({ ...fd, [name]: value }));
    }
  };
  const handleFileChange = (e, isBlog = false) => {
    const file = e.target.files[0];
    if (isBlog) {
      setBlogFormData((fd) => ({ ...fd, image: file }));
    } else {
      setProductFormData((fd) => ({ ...fd, image: file }));
    }
  };

  // --- Save handlers ---
  const handleSaveProduct = async () => {
    try {
      const formDataObj = new FormData();
      Object.entries(productFormData).forEach(([k, v]) =>
        formDataObj.append(k, v)
      );
      const url = selectedProduct
        ? `https://dawamaxma.dawamtaani.co.ke/api/pharmacy/${selectedProduct.id}`
        : 'https://dawamaxma.dawamtaani.co.ke/api/pharmacy';
      const method = selectedProduct ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, body: formDataObj });
      if (!res.ok) throw new Error('Save failed');
      const saved = await res.json();

      if (selectedProduct) {
        setProducts((ps) =>
          ps.map((p) => (p.id === saved.id ? saved : p))
        );
        setFilteredProducts((ps) =>
          ps.map((p) => (p.id === saved.id ? saved : p))
        );
        toast.success('Product updated');
      } else {
        setProducts((ps) => [...ps, saved]);
        setFilteredProducts((ps) => [...ps, saved]);
        toast.success('Product added');
      }
      handleCloseModals();
    } catch {
      toast.error('Failed to save product');
    }
  };

  const handleSaveBlog = async () => {
    try {
      const formDataObj = new FormData();
      Object.entries(blogFormData).forEach(([k, v]) =>
        formDataObj.append(k, v)
      );
      const url = selectedBlog
        ? `https://dawamaxma.dawamtaani.co.ke/api/blogs/${selectedBlog.id}`
        : 'https://dawamaxma.dawamtaani.co.ke/api/blogs';
      const method = selectedBlog ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, body: formDataObj });
      if (!res.ok) throw new Error('Save failed');
      const saved = await res.json();

      if (selectedBlog) {
        setBlogs((bs) => bs.map((b) => (b.id === saved.id ? saved : b)));
        toast.success('Blog updated');
      } else {
        setBlogs((bs) => [...bs, saved]);
        toast.success('Blog added');
      }
      handleCloseModals();
    } catch {
      toast.error('Failed to save blog');
    }
  };

  // --- Paginated slices ---
  const paginatedProducts = filteredProducts.slice(
    (productPage - 1) * PRODUCTS_PER_PAGE,
    productPage * PRODUCTS_PER_PAGE
  );
  const paginatedBlogs = blogs.slice(
    (blogPage - 1) * BLOGS_PER_PAGE,
    blogPage * BLOGS_PER_PAGE
  );

  if (loading) return <PathFinderLoader />;
  if (error) return <div className="text-center text-red-600">{error}</div>;

  return (
    <div className="container mx-auto px-4 py-8 font-sans">
      <h1 className="text-3xl font-bold mb-6 text-center">
        Admin Panel – Products & Blogs
      </h1>

      <TabNavigation
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />

      {selectedTab === 1 && (
        <ProductSection
          products={paginatedProducts}
          categories={categories}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          onEdit={handleOpenEditModal}
          onAdd={handleOpenAddModal}
          currentPage={productPage}
          totalPages={totalProductPages}
          onPageChange={setProductPage}
        />
      )}

      {selectedTab === 2 && (
        <BlogSection
          blogs={paginatedBlogs}
          onEdit={handleOpenBlogEditModal}
          onAdd={handleOpenBlogAddModal}
          currentPage={blogPage}
          totalPages={totalBlogPages}
          onPageChange={setBlogPage}
        />
      )}

      {/* Product/Blog Modals */}
      {(isEditModalOpen || isAddModalOpen) && (
        <Modal
          isOpen={isEditModalOpen || isAddModalOpen}
          onClose={handleCloseModals}
          title={isEditModalOpen ? 'Edit Product' : 'Add Product'}
          formData={productFormData}
          onInputChange={handleInputChange}
          onFileChange={(e) => handleFileChange(e, false)}
          onSave={handleSaveProduct}
          isBlog={false}
          categories={categories}
        />
      )}
      {(isBlogEditModalOpen || isBlogAddModalOpen) && (
        <Modal
          isOpen={isBlogEditModalOpen || isBlogAddModalOpen}
          onClose={handleCloseModals}
          title={isBlogEditModalOpen ? 'Edit Blog' : 'Add Blog'}
          formData={blogFormData}
          onInputChange={(e) => handleInputChange(e, true)}
          onFileChange={(e) => handleFileChange(e, true)}
          onSave={handleSaveBlog}
          isBlog={true}
        />
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

// --- Tab Navigation ---
const TabNavigation = ({ selectedTab, setSelectedTab }) => {
  const tabs = [
    { id: 1, title: 'Products' },
    { id: 2, title: 'Blogs' },
  ];
  return (
    <div className="flex justify-center mb-6">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => setSelectedTab(tab.id)}
          className={`mx-2 px-4 py-2 rounded font-semibold ${
            selectedTab === tab.id
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-200 text-gray-700'
          }`}
        >
          {tab.title}
        </button>
      ))}
    </div>
  );
};

// --- Product Section with Pagination & Jump (no onScroll) ---
const ProductSection = ({
  products,
  categories,
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  onEdit,
  onAdd,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const [jumpPage, setJumpPage] = useState('');

  return (
    <div>
      <div className="flex flex-wrap justify-between mb-4">
        <div className="flex space-x-2 mb-2">
          <input
            type="text"
            placeholder="Search products…"
            className="border p-2 rounded"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select
            className="border p-2 rounded"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <button
          className="bg-orange-500 text-white px-4 py-2 rounded"
          onClick={onAdd}
        >
          Add Product
        </button>
      </div>

      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto border rounded-lg">
        <table className="min-w-full bg-white">
          <thead className="sticky top-0 bg-gray-100">
            <tr>
              {['ID', 'Image', 'Name', 'Category', 'Price', 'Created', 'Action'].map(
                (h) => (
                  <th key={h} className="px-2 py-1 border">
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="text-sm">
                <td className="px-2 py-1 border">{p.id}</td>
                <td className="px-2 py-1 border">
                  <img
                    src={getProductImage(p, '/default-image-path')}
                    alt={p.itemName}
                    className="w-12 h-12 rounded object-cover"
                  />
                </td>
                <td className="px-2 py-1 border">{p.itemName}</td>
                <td className="px-2 py-1 border">{p.category}</td>
                <td className="px-2 py-1 border">
                  Ksh {parseFloat(p.retailPrice).toFixed(2)}
                </td>
                <td className="px-2 py-1 border">{p.createdAt}</td>
                <td className="px-2 py-1 border">
                  <button
                    className="bg-blue-500 text-white px-2 py-1 rounded"
                    onClick={() => onEdit(p)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Prev / Next / Jump */}
      <div className="flex justify-center items-center mt-4 space-x-4">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Prev
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
        <div className="flex items-center space-x-2">
          <label>Jump to:</label>
          <input
            type="number"
            min="1"
            max={totalPages}
            value={jumpPage}
            onChange={(e) => setJumpPage(e.target.value)}
            className="border p-1 w-16 text-center rounded"
          />
          <button
            onClick={() => {
              const p = parseInt(jumpPage, 10);
              if (!isNaN(p) && p >= 1 && p <= totalPages) {
                onPageChange(p);
                setJumpPage('');
              }
            }}
            className="px-3 py-1 bg-gray-200 rounded"
          >
            Go
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Blog Section with Pagination & Jump ---
const BlogSection = ({
  blogs,
  onEdit,
  onAdd,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const [jumpPage, setJumpPage] = useState('');

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h2 className="text-2xl font-bold">Blogs</h2>
        <button
          className="bg-green-500 text-white px-4 py-2 rounded"
          onClick={onAdd}
        >
          Add Blog
        </button>
      </div>
      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto border rounded-lg">
        <table className="min-w-full bg-white">
          <thead className="sticky top-0 bg-gray-100">
            <tr>
              {['ID', 'Image', 'Title', 'Subheading', 'Created', 'Action'].map(
                (h) => (
                  <th key={h} className="px-2 py-1 border">
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {blogs.map((b) => (
              <tr key={b.id} className="text-sm">
                <td className="px-2 py-1 border">{b.id}</td>
                <td className="px-2 py-1 border">
                  <img
                    src={
                      b.image
                        ? `https://dawamaxma.dawamtaani.co.ke${b.image}`
                        : '/default-image-path'
                    }
                    alt={b.title}
                    className="w-12 h-12 rounded object-cover"
                  />
                </td>
                <td className="px-2 py-1 border">{b.title}</td>
                <td className="px-2 py-1 border">{b.subheading}</td>
                <td className="px-2 py-1 border">{b.created_at}</td>
                <td className="px-2 py-1 border">
                  <button
                    className="bg-blue-500 text-white px-2 py-1 rounded"
                    onClick={() => onEdit(b)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Prev / Next / Jump */}
      <div className="flex justify-center items-center mt-4 space-x-4">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Prev
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
        <div className="flex items-center space-x-2">
          <label>Jump to:</label>
          <input
            type="number"
            min="1"
            max={totalPages}
            value={jumpPage}
            onChange={(e) => setJumpPage(e.target.value)}
            className="border p-1 w-16 text-center rounded"
          />
          <button
            onClick={() => {
              const p = parseInt(jumpPage, 10);
              if (!isNaN(p) && p >= 1 && p <= totalPages) {
                onPageChange(p);
                setJumpPage('');
              }
            }}
            className="px-3 py-1 bg-gray-200 rounded"
          >
            Go
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Reusable Modal for Product & Blog ---
const Modal = ({
  isOpen,
  onClose,
  title,
  formData,
  onInputChange,
  onFileChange,
  onSave,
  isBlog,
  categories = [],
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white w-full max-w-lg p-6 rounded-lg relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
        >
          <X size={24} />
        </button>
        <h2 className="text-2xl font-bold text-center mb-4">{title}</h2>
        <div className="space-y-4">
          {!isBlog ? (
            <>
              <input
                type="text"
                name="itemName"
                value={formData.itemName}
                onChange={onInputChange}
                placeholder="Product Name"
                className="border p-2 rounded w-full"
              />
              <select
                name="category"
                value={formData.category}
                onChange={onInputChange}
                className="border p-2 rounded w-full"
              >
                <option value="">Select Category</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <input
                type="number"
                name="retailPrice"
                value={formData.retailPrice}
                onChange={onInputChange}
                placeholder="Price"
                className="border p-2 rounded w-full"
              />
              <textarea
                name="description"
                value={formData.description}
                onChange={onInputChange}
                placeholder="Description"
                rows="3"
                className="border p-2 rounded w-full"
              />
              <textarea
                name="precautions"
                value={formData.precautions}
                onChange={onInputChange}
                placeholder="Precautions"
                rows="3"
                className="border p-2 rounded w-full"
              />
            </>
          ) : (
            <>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={onInputChange}
                placeholder="Title"
                className="border p-2 rounded w-full"
              />
              <input
                type="text"
                name="subheading"
                value={formData.subheading}
                onChange={onInputChange}
                placeholder="Subheading"
                className="border p-2 rounded w-full"
              />
              <textarea
                name="content"
                value={formData.content}
                onChange={onInputChange}
                placeholder="Content"
                rows="4"
                className="border p-2 rounded w-full"
              />
            </>
          )}
          <input
            type="file"
            onChange={onFileChange}
            className="border p-2 rounded w-full"
          />
          {formData.image && (
            <img
              src={
                typeof formData.image === 'string'
                  ? `https://dawamaxma.dawamtaani.co.ke${formData.image}`
                  : URL.createObjectURL(formData.image)
              }
              alt="Preview"
              className="w-24 h-24 rounded object-cover"
            />
          )}
        </div>
        <button
          onClick={onSave}
          className="mt-6 w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default AdminPanel;
